import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtTons, fmtMoney, monthLabel } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Download, FileSpreadsheet, TrendingUp, ClipboardList, ChevronRight } from "lucide-react";

const nowMonth = () => `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;

export default function Reports() {
  const navigate = useNavigate();
  const [fys, setFys] = useState([]);
  const [scope, setScope] = useState("month"); // month | fy | range
  const [fy, setFy] = useState("");
  const [month, setMonth] = useState(nowMonth());
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [status, setStatus] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/reports/financial-years").then((r) => {
      setFys(r.data);
      if (r.data.length && !fy) setFy(r.data[0]);
    });
  }, []); // eslint-disable-line

  const params = useCallback(() => {
    if (scope === "fy") return fy ? { fy } : null;
    if (scope === "range") return start && end ? { start, end } : null;
    return month ? { month } : null;
  }, [scope, fy, month, start, end]);

  const loadAnalytics = useCallback(async () => {
    const p = params();
    if (!p) { setAnalytics(null); return; }
    const { data } = await api.get("/reports/analytics", { params: p });
    setAnalytics(data);
  }, [params]);

  const loadStatus = useCallback(async () => {
    // Pending/Completed is month-based
    const m = scope === "month" ? month : nowMonth();
    const { data } = await api.get("/reports/bills-status", { params: { month: m } });
    setStatus(data);
  }, [scope, month]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const doExport = async () => {
    const p = params();
    if (!p) { toast.error("Choose a valid period first"); return; }
    setExporting(true);
    try {
      const { data, headers } = await api.get("/reports/export", { params: { type: scope, ...p }, responseType: "blob" });
      const cd = headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : "SFX_Sales.xlsx";
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const openBill = (row) => navigate(`/admin/billing?customer=${encodeURIComponent(row.customer)}&month=${row.month}`);
  const openCustomer = (c) => c.customer_id && navigate(`/admin/customers/${c.customer_id}`);

  const periodValid = !!params();

  return (
    <div data-testid="reports-page">
      <h1 className="font-chivo font-black text-3xl text-slate-900">Reports</h1>
      <p className="text-slate-500 mt-1 mb-6">View and export your sales data by month, financial year or a custom date range.</p>

      {/* Period filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="w-full md:w-48">
            <Label className="text-xs">Report Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="report-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="fy">Financial Year</SelectItem>
                <SelectItem value="range">Custom Date Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scope === "month" && (
            <div className="w-full md:w-56">
              <Label className="text-xs">Month</Label>
              <Input type="month" data-testid="report-month-input" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
          )}
          {scope === "fy" && (
            <div className="w-full md:w-56">
              <Label className="text-xs">Financial Year (1 Apr – 31 Mar)</Label>
              <Select value={fy} onValueChange={setFy}>
                <SelectTrigger data-testid="report-fy-select"><SelectValue placeholder="Select FY" /></SelectTrigger>
                <SelectContent>
                  {fys.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No data yet</div>}
                  {fys.map((f) => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {scope === "range" && (
            <>
              <div className="w-full md:w-44"><Label className="text-xs">From</Label><Input type="date" data-testid="report-start-input" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} /></div>
              <div className="w-full md:w-44"><Label className="text-xs">To</Label><Input type="date" data-testid="report-end-input" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} /></div>
            </>
          )}

          <div className="md:ml-auto">
            <Button data-testid="export-excel-button" onClick={doExport} disabled={!periodValid || exporting} className="bg-emerald-600 hover:bg-emerald-700 w-full md:w-auto">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> {exporting ? "Preparing…" : "Export to Excel"}
            </Button>
          </div>
        </div>
        {analytics && <p className="text-xs text-slate-500 mt-3">Showing: <span className="font-semibold text-slate-700">{analytics.period_label}</span></p>}
      </div>

      <Tabs defaultValue="sales">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="sales" data-testid="tab-sales"><TrendingUp className="h-4 w-4 mr-1.5" />Sales Report</TabsTrigger>
          <TabsTrigger value="bills" data-testid="tab-bills"><ClipboardList className="h-4 w-4 mr-1.5" />Pending / Completed Bills</TabsTrigger>
        </TabsList>

        {/* SALES REPORT */}
        <TabsContent value="sales" className="mt-5">
          {!analytics ? <p className="text-slate-400">Select a valid period to see the report.</p> : (
            <div className="space-y-6">
              {/* Top totals */}
              <div className="grid gap-4 sm:grid-cols-3" data-testid="sales-totals">
                <div className="bg-slate-900 text-white rounded-lg p-5 shadow-sm">
                  <div className="text-slate-300 text-xs font-semibold uppercase tracking-wide">Total Tons</div>
                  <div className="text-3xl font-chivo font-black tabular mt-2">{fmtTons(analytics.total_tons)} <span className="text-base">t</span></div>
                </div>
                <div className="bg-emerald-600 text-white rounded-lg p-5 shadow-sm">
                  <div className="text-emerald-100 text-xs font-semibold uppercase tracking-wide">Total Sales</div>
                  <div className="text-3xl font-chivo font-black tabular mt-2">{fmtMoney(analytics.total_amount)}</div>
                </div>
                <div className="bg-sky-600 text-white rounded-lg p-5 shadow-sm">
                  <div className="text-sky-100 text-xs font-semibold uppercase tracking-wide">Transactions</div>
                  <div className="text-3xl font-chivo font-black tabular mt-2">{analytics.count}</div>
                </div>
              </div>

              {/* Plant split — SFX 1 on top, SFX 2 below */}
              <div className="grid gap-4" data-testid="plant-split">
                {[{ k: "sfx1", label: "SFX 1", cls: "from-sky-500 to-sky-600" }, { k: "sfx2", label: "SFX 2", cls: "from-indigo-500 to-indigo-600" }].map((pl) => (
                  <div key={pl.k} className={`bg-gradient-to-br ${pl.cls} text-white rounded-lg p-5 shadow-sm`} data-testid={`plant-card-${pl.k}`}>
                    <div className="font-semibold uppercase tracking-wide text-sm opacity-90">{pl.label}</div>
                    <div className="flex items-end gap-6 mt-3">
                      <div><div className="text-xs opacity-80">Tons</div><div className="text-2xl font-chivo font-black tabular">{fmtTons(analytics[pl.k].tons)} t</div></div>
                      <div><div className="text-xs opacity-80">Sales</div><div className="text-2xl font-chivo font-black tabular">{fmtMoney(analytics[pl.k].amount)}</div></div>
                      <div><div className="text-xs opacity-80">Txns</div><div className="text-2xl font-chivo font-black tabular">{analytics[pl.k].count}</div></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Customer-wise */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-800">Customer-wise Sales</div>
                <table className="w-full text-sm" data-testid="customer-wise-table">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr><th className="p-3 font-semibold">Customer</th><th className="p-3 text-right font-semibold">Tons</th><th className="p-3 text-right font-semibold">Sales</th><th className="p-3 text-right font-semibold">Txns</th><th className="p-3"></th></tr>
                  </thead>
                  <tbody>
                    {analytics.customers.map((c) => (
                      <tr key={c.customer} className={`border-t border-slate-100 ${c.customer_id ? "cursor-pointer hover:bg-sky-50/50" : ""}`}
                        onClick={() => openCustomer(c)} data-testid={`customer-row-${c.customer}`}>
                        <td className="p-3 font-medium text-slate-900">{c.customer}</td>
                        <td className="p-3 text-right tabular">{fmtTons(c.tons)} t</td>
                        <td className="p-3 text-right tabular font-semibold">{c.amount > 0 ? fmtMoney(c.amount) : <span className="text-slate-300">not billed</span>}</td>
                        <td className="p-3 text-right tabular">{c.count}</td>
                        <td className="p-3 text-right">{c.customer_id && <ChevronRight className="h-4 w-4 text-slate-300 inline" />}</td>
                      </tr>
                    ))}
                    {analytics.customers.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-slate-400">No sales in this period.</td></tr>}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400">Sales amounts are calculated from each customer's saved monthly-bill rate. Months without a saved bill show tons only.</p>
            </div>
          )}
        </TabsContent>

        {/* PENDING / COMPLETED BILLS */}
        <TabsContent value="bills" className="mt-5">
          {scope !== "month" && <p className="text-sm text-amber-600 mb-4">Bill status is tracked per month. Showing {monthLabel(nowMonth())}. Switch scope to “Monthly” and pick a month to change this.</p>}
          {!status ? <p className="text-slate-400">Loading…</p> : (
            <div className="space-y-6" data-testid="bills-status">
              <div className="flex flex-wrap gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-3">
                  <div className="text-amber-700 text-xs font-semibold uppercase tracking-wide">Pending</div>
                  <div className="text-3xl font-chivo font-black text-amber-700 tabular" data-testid="pending-count">{status.pending_count}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-3">
                  <div className="text-emerald-700 text-xs font-semibold uppercase tracking-wide">Completed / Printed</div>
                  <div className="text-3xl font-chivo font-black text-emerald-700 tabular" data-testid="completed-count">{status.completed_count}</div>
                </div>
                <div className="self-center text-sm text-slate-500">for <span className="font-semibold text-slate-700">{monthLabel(status.month)}</span></div>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                  <div className="px-4 py-3 border-b border-slate-200 font-semibold text-amber-700 flex items-center gap-2">Pending Bills</div>
                  <table className="w-full text-sm" data-testid="pending-table">
                    <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="p-3 font-semibold">Customer</th><th className="p-3 text-right font-semibold">Tons</th><th className="p-3 text-right font-semibold">Status</th></tr></thead>
                    <tbody>
                      {status.pending.map((r) => (
                        <tr key={r.customer} className="border-t border-slate-100 cursor-pointer hover:bg-amber-50/50" onClick={() => openBill(r)} data-testid={`pending-row-${r.customer}`}>
                          <td className="p-3 font-medium text-slate-900">{r.customer}</td>
                          <td className="p-3 text-right tabular">{fmtTons(r.tons)} t</td>
                          <td className="p-3 text-right"><span className="inline-block bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">PENDING</span></td>
                        </tr>
                      ))}
                      {status.pending.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-slate-400">No pending bills 🎉</td></tr>}
                    </tbody>
                  </table>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                  <div className="px-4 py-3 border-b border-slate-200 font-semibold text-emerald-700 flex items-center gap-2">Completed / Printed</div>
                  <table className="w-full text-sm" data-testid="completed-table">
                    <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="p-3 font-semibold">Customer</th><th className="p-3 text-right font-semibold">Bill Amount</th><th className="p-3 text-right font-semibold">Status</th></tr></thead>
                    <tbody>
                      {status.completed.map((r) => (
                        <tr key={r.customer} className="border-t border-slate-100 cursor-pointer hover:bg-emerald-50/50" onClick={() => openBill(r)} data-testid={`completed-row-${r.customer}`}>
                          <td className="p-3 font-medium text-slate-900">{r.customer}</td>
                          <td className="p-3 text-right tabular font-semibold">{fmtMoney(r.amount)}</td>
                          <td className="p-3 text-right"><span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-0.5 rounded-full">COMPLETED</span></td>
                        </tr>
                      ))}
                      {status.completed.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-slate-400">No completed bills yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
